import { KeyRound, Loader2, FileText, Download, Home, MapPin, Users, Building2 } from "lucide-react";
import { PropertyValuation } from "@/components/PropertyValuation";
import { formatQuota, DirittoBadge } from "@/components/CatastoPanel";

// Sezione "Il mio immobile" del portale: codice fiscale, visure acquistate, unità intestate e visura a pagamento.
export const PortalImmobile = ({ data, curBuilding, cfInput, setCfInput, saveCf, cfBusy, cfMsg, visuraMsg, visureMine, downloadVisura, mieUnita, visuraBusy, buyVisura, authToken }) => (
  <div data-testid="portal-immobile-section">
    <h1 className="font-head text-2xl font-extrabold tracking-tight mb-1">Il mio immobile</h1>
    <p className="text-sm text-muted-foreground mb-6">Dati catastali del tuo condominio e visura del tuo immobile, relativa all'indirizzo di <span className="font-medium text-foreground/90">{curBuilding?.nome || "questo condominio"}</span>.</p>

    <div className={`mb-6 rounded-2xl p-4 border ${data?.codice_fiscale ? "bg-card border-border" : "bg-[#007BFF]/5 border-[#007BFF]/30"}`} data-testid="portal-cf-card">
      <h2 className="font-semibold text-sm flex items-center gap-2 mb-1"><KeyRound className="h-4 w-4 text-[#007BFF]" />Il tuo codice fiscale</h2>
      {data?.codice_fiscale ? (
        <>
          <p className="text-sm">Registrato: <span className="font-mono font-semibold" data-testid="portal-cf-value">{data.codice_fiscale}</span></p>
          <p className="text-[11px] text-muted-foreground mt-1">Sincronizzato con l'amministratore. Se è errato puoi aggiornarlo qui sotto.</p>
        </>
      ) : (
        <p className="text-xs text-muted-foreground">Inserisci il tuo codice fiscale: verrà sincronizzato automaticamente con l'amministratore e permetterà di abbinare e mostrare qui il tuo immobile.</p>
      )}
      <div className="mt-2 flex flex-col sm:flex-row gap-2">
        <input data-testid="portal-cf-input" value={cfInput} onChange={(e) => setCfInput(e.target.value.toUpperCase())} maxLength={16}
          placeholder={data?.codice_fiscale || "RSSMRA80A01H501U"}
          className="flex-1 bg-secondary border border-border rounded-lg px-3 py-2.5 text-sm font-mono uppercase outline-none focus:border-[#007BFF]" />
        <button data-testid="portal-cf-save" onClick={saveCf} disabled={cfBusy || cfInput.trim().length < 11}
          className="inline-flex items-center justify-center gap-1.5 bg-[#007BFF] hover:bg-[#0069D9] text-white text-sm font-semibold rounded-lg px-5 py-2.5 transition-colors disabled:opacity-50">
          {cfBusy && <Loader2 className="h-4 w-4 animate-spin" />}{data?.codice_fiscale ? "Aggiorna" : "Salva"}
        </button>
      </div>
      {cfMsg && <p className="text-[11px] mt-2 text-[#007BFF]" data-testid="portal-cf-msg">{cfMsg}</p>}
    </div>

    {visuraMsg && (
      <div className="mb-4 flex items-start gap-2 bg-[#007BFF]/10 border border-[#007BFF]/30 rounded-xl px-3 py-2.5 text-sm" data-testid="visura-msg">
        <FileText className="h-4 w-4 text-[#007BFF] shrink-0 mt-0.5" /><span>{visuraMsg}</span>
      </div>
    )}

    {visureMine.length > 0 && (
      <div className="mb-6" data-testid="visure-acquistate">
        <h2 className="font-semibold mb-2 text-sm">Visure acquistate</h2>
        <div className="space-y-2">
          {visureMine.map((v) => (
            <div key={v.session_id} className="bg-card border border-border rounded-xl p-3 flex items-center gap-3" data-testid={`visura-owned-${v.session_id}`}>
              <div className="h-9 w-9 rounded-lg bg-green-500/15 flex items-center justify-center shrink-0"><FileText className="h-4 w-4 text-green-500" /></div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{v.unit_label || "Immobile"}</div>
                <div className="text-[11px] text-muted-foreground">Acquistata il {(v.paid_at || v.created_at || "").slice(0, 10)}</div>
              </div>
              <button data-testid={`visura-download-${v.session_id}`} onClick={() => downloadVisura(v.session_id, v.unit_label)}
                className="shrink-0 inline-flex items-center gap-1.5 bg-secondary hover:bg-muted border border-border text-sm font-medium rounded-full px-3.5 py-2 transition-colors">
                <Download className="h-4 w-4" />PDF
              </button>
            </div>
          ))}
        </div>
      </div>
    )}

    {mieUnita.length > 0 ? (
      <div className="mb-6" data-testid="mie-unita">
        <h2 className="font-semibold mb-2 text-sm flex items-center gap-2">
          <Home className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />Il tuo immobile
        </h2>
        <div className="space-y-2.5">
          {mieUnita.map((u, i) => (
            <div key={u.id_immobile || `mine-${i}`} data-testid={`mia-unita-${i}`} className="bg-emerald-50 border border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/30 rounded-2xl p-4">
              <div className="font-semibold text-sm flex items-center gap-2 flex-wrap text-emerald-900 dark:text-emerald-100">
                <span>Fg. {u.foglio || "—"} · Part. {u.particella || "—"}{u.subalterno ? ` · Sub. ${u.subalterno}` : ""}</span>
                {u.categoria && <span className="text-[10px] bg-white/70 border border-emerald-200 text-emerald-800 dark:bg-emerald-500/20 dark:border-emerald-500/40 dark:text-emerald-100 rounded-full px-2 py-0.5">{u.categoria}</span>}
                <span className="text-[10px] bg-emerald-600 text-white dark:bg-emerald-500 rounded-full px-2 py-0.5 font-semibold uppercase tracking-wide">Intestato a te</span>
                <DirittoBadge titolarita={u.titolarita} />
              </div>
              {u.indirizzo && <div className="text-xs text-emerald-800/80 dark:text-emerald-200/80 mt-1 flex items-start gap-1"><MapPin className="h-3 w-3 shrink-0 mt-0.5" />{u.indirizzo}</div>}
              <div className="text-[11px] text-emerald-800/70 dark:text-emerald-200/70 mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
                {u.classe && <span>Classe {u.classe}</span>}
                {u.consistenza && <span>Consistenza {u.consistenza}</span>}
                {u.rendita && <span>Rendita {u.rendita}</span>}
                {u.titolarita && <span>{formatQuota(u.titolarita)}</span>}
              </div>
              {(u.comproprietari || []).length > 0 && (
                <div className="mt-3 pt-3 border-t border-emerald-200/70 dark:border-emerald-500/25" data-testid={`mia-unita-comproprietari-${i}`}>
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700/80 dark:text-emerald-300/80 mb-1 flex items-center gap-1">
                    <Users className="h-3 w-3" />Comproprietari
                  </div>
                  <div className="flex flex-col gap-1">
                    {u.comproprietari.map((c, ci) => (
                      <div key={ci} data-testid={`comproprietario-${i}-${ci}`} className="text-[11px] text-emerald-900/90 dark:text-emerald-100/90 flex items-center gap-1.5 flex-wrap">
                        <span className="font-medium">{c.nome || "—"}</span>
                        {c.titolarita && <span className="text-emerald-700/70 dark:text-emerald-300/70">· {formatQuota(c.titolarita)}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-3 flex justify-end">
                <button data-testid={`mia-visura-buy-${i}`} disabled={!!visuraBusy || !u.id_immobile} onClick={() => buyVisura(u.id_immobile)}
                  className="inline-flex items-center gap-1.5 bg-[#007BFF] hover:bg-[#0069D9] text-white text-sm font-semibold rounded-full px-4 py-2 transition-colors disabled:opacity-50">
                  {visuraBusy === u.id_immobile ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}Visura PDF · €8
                </button>
              </div>
              {u.id_immobile && (
                <PropertyValuation token={authToken} buildingId={curBuilding?.id} idImmobile={u.id_immobile} index={i} />
              )}
            </div>
          ))}
        </div>
      </div>
    ) : (
      <div className="text-center py-10 text-muted-foreground flex flex-col items-center gap-3 bg-card border border-border rounded-2xl" data-testid="immobile-empty">
        <Building2 className="h-9 w-9 opacity-40" />
        <span className="text-sm">Nessun immobile risulta ancora intestato a te.<br />Inserisci o verifica il tuo codice fiscale qui sopra: l'amministratore lo abbinerà al tuo immobile.</span>
      </div>
    )}
    <p className="text-[11px] text-muted-foreground/70 mt-4">Il pagamento (€8,00, IVA inclusa) avviene con carta tramite Stripe. Al termine la visura viene generata e potrai scaricarla dalla sezione "Visure acquistate".</p>
  </div>
);
